import { PlayerPool } from "@/features/pickem/components/player-pool";
import { personalBracket, prunePicks, type LineupSlot } from "@fq/pickem-ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import PickemPlaceholderUrl from "@/features/pickem/assets/pickem-placeholder.webp?url";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trophy, Loader2, Lock, Share2 } from "lucide-react";
import { toast } from "sonner";
import {
  ScoreSelector,
  SharePickemDialog,
  SubmitPicksButton,
  SubmitPicksCelebrationDialog,
  usePickemSubmitted,
} from "@fq/pickem-ui";
import {
  getPickemEventDetailOptions,
  getPickemUserBracketOptions,
  getPickemUserBracketQueryKey,
  getPickemLeaderboardOptions,
  submitPickemPicksMutation,
} from "@api/generated/@tanstack/react-query.gen";
import type {
  PickemBracketMatch,
  PickemCompetitor,
  PickemEvent,
  PickemUserPick,
} from "@api/generated/types.gen";
import { getStoredToken } from "@api/api-client-config";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  PickemBracket,
  type PickemBracketPickContext,
} from "@/features/pickem/components/pickem-bracket";
import { computeEffectiveCompetitors } from "@/features/pickem/lib/pickem-simulation";
import { PickemLeaderboard } from "@/features/pickem/components/pickem-leaderboard";
import { PickemScoringInfo } from "@/features/pickem/components/pickem-scoring-info";
import {
  effectiveStatusBadgeClass,
  effectiveStatusLabel,
  formatRelativeTime,
  getEffectiveStatus,
} from "@/features/pickem/lib/pickem-status";
import { useAuth } from "@/contexts/auth-context";
import {
  draftPreviewEnabled,
  previewBracket,
  savePreviewPicks,
} from "@/features/pickem/lib/draft-preview";
import { GoogleSignInButton } from "@/components/auth/google-sign-in-button";
import { cn } from "@/lib/utils";

// Share URLs must resolve from the public internet so Twitter's crawler can
// unfurl them — always point at the deployed API even from local dev.
const PROD_API_URL =
  "https://dxpuw6f7o9.execute-api.eu-central-1.amazonaws.com/dev/";
const PUBLIC_API_BASE_URL = (
  import.meta.env.VITE_API_URL ?? PROD_API_URL
).replace(/\/$/, "");

/**
 * Identifies the "finals" match in a bracket:
 * - Double-elim: the lone match in section='grand_final'
 * - Single-elim: highest-round match in section='main'
 * Mirrors the backend's findFinalsMatchId — keep them in sync.
 */
function findFinalsMatchId(matches: PickemBracketMatch[]): string | null {
  const grandFinal = matches.find((m) => m.section === "grand_final");
  if (grandFinal) return grandFinal.id;
  const mainMatches = matches.filter((m) => m.section === "main");
  if (mainMatches.length === 0) return null;
  const maxRound = Math.max(...mainMatches.map((m) => m.roundNumber));
  return mainMatches.find((m) => m.roundNumber === maxRound)?.id ?? null;
}

/**
 * Identifies the two matches whose winners feed into the finals — needed
 * to check whether the user predicted the exact finals matchup. Mirrors
 * the backend helper of the same name.
 */
function findFinalsFeederMatchIds(
  matches: PickemBracketMatch[],
  finalsMatchId: string,
): string[] | null {
  const finalsMatch = matches.find((m) => m.id === finalsMatchId);
  if (!finalsMatch) return null;
  if (finalsMatch.section === "grand_final") {
    const upper = matches.filter((m) => m.section === "upper");
    const lower = matches.filter((m) => m.section === "lower");
    if (!upper.length || !lower.length) return null;
    const upperMax = Math.max(...upper.map((m) => m.roundNumber));
    const lowerMax = Math.max(...lower.map((m) => m.roundNumber));
    const upperFinal = upper.find((m) => m.roundNumber === upperMax);
    const lowerFinal = lower.find((m) => m.roundNumber === lowerMax);
    if (!upperFinal || !lowerFinal) return null;
    return [upperFinal.id, lowerFinal.id];
  }
  const semiRound = finalsMatch.roundNumber - 1;
  if (semiRound < 1) return null;
  const semis = matches
    .filter(
      (m) => m.section === finalsMatch.section && m.roundNumber === semiRound,
    )
    .sort((a, b) => a.matchIndex - b.matchIndex);
  return semis.length >= 2 ? semis.map((m) => m.id) : null;
}

/** Flat Grand Final bonus — mirrors GRAND_FINAL_SCORE_BONUS on the backend. */
const GRAND_FINAL_SCORE_BONUS = 5;

/**
 * Per-match points the user earned for this event — same formula the backend
 * uses (see api/src/pickem/services/pickem-scoring.ts). Keep them in sync:
 * - Double-elim Grand Final: winner pick alone earns round points (no matchup
 *   gate). Flat +5 when BOTH the reset and the exact loser score are correct.
 * - Single-elim final: strict matchup gate + sliding loser-score bonus.
 * Returned as a Map<matchId → points>; matches not in the map earned 0.
 */
function computePointsByMatchId(
  matches: PickemBracketMatch[],
  userPicks: Record<string, string>,
  userLoserScores: Record<string, number | null>,
  userResets: Record<string, boolean | null>,
  pointsPerRound: Record<string, number> | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  const scoring = pointsPerRound ?? {
    "1": 1,
    "2": 2,
    "3": 4,
    "4": 8,
    "5": 16,
    "6": 32,
  };
  const finalsId = findFinalsMatchId(matches);

  for (const match of matches) {
    if (match.status !== "completed" || !match.winnerId) continue;
    const pick = userPicks[match.id];
    if (!pick || pick !== match.winnerId) continue;

    if (finalsId && match.id === finalsId) {
      // Grand Final: no matchup gate — the opponent doesn't matter. Round
      // points for the winner, plus a flat +5 when reset AND score both match.
      if (match.section === "grand_final") {
        let pts = scoring[String(match.roundNumber)] ?? 1;
        const resetOk =
          match.actualReset != null &&
          userResets[match.id] != null &&
          userResets[match.id] === match.actualReset;
        const scoreOk =
          match.actualLoserScore != null &&
          userLoserScores[match.id] != null &&
          userLoserScores[match.id] === match.actualLoserScore;
        if (resetOk && scoreOk) pts += GRAND_FINAL_SCORE_BONUS;
        out[match.id] = pts;
        continue;
      }

      // Single-elim final: strict matchup gate — both finalists must be the
      // user's predicted finalists (or feeders unavailable, e.g. 2-team).
      const feeders = findFinalsFeederMatchIds(matches, match.id);
      if (feeders) {
        const predicted = new Set(
          feeders.map((id) => userPicks[id]).filter((v): v is string => !!v),
        );
        const actual = new Set(
          [match.competitorTopId, match.competitorBottomId].filter(
            (v): v is string => !!v,
          ),
        );
        const matchupOk =
          predicted.size === actual.size &&
          [...actual].every((id) => predicted.has(id));
        if (!matchupOk) continue;
      }
      let pts = scoring[String(match.roundNumber)] ?? 1;
      const predictedLoser = userLoserScores[match.id];
      if (predictedLoser != null && match.actualLoserScore != null) {
        pts += Math.max(
          0,
          5 - Math.abs(predictedLoser - match.actualLoserScore),
        );
      }
      out[match.id] = pts;
      continue;
    }

    out[match.id] = scoring[String(match.roundNumber)] ?? 1;
  }
  return out;
}

export const Route = createFileRoute("/pickem/$eventId")({
  component: PickemEventDetailPage,
});

interface BracketPayload {
  event: PickemEvent;
  competitors: PickemCompetitor[];
  bracketMatches: PickemBracketMatch[];
  userPicks: PickemUserPick[];
}

function PickemEventDetailPage() {
  const { eventId } = Route.useParams();
  const auth = useAuth();
  const isAuthenticated = draftPreviewEnabled || auth.isAuthenticated;
  const authLoading = !draftPreviewEnabled && auth.isLoading;
  const user = auth.user;
  const queryClient = useQueryClient();

  // Auth-dependent: pull the user's bracket (which embeds picks + score) when
  // signed in; fall back to the strictly-public event detail otherwise.
  const userBracketQuery = useQuery({
    ...getPickemUserBracketOptions({ path: { eventId } }),
    ...(draftPreviewEnabled ? { queryFn: () => previewBracket(eventId) } : {}),
    enabled: isAuthenticated,
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000,
    staleTime: 0,
  });
  const publicDetailQuery = useQuery({
    ...getPickemEventDetailOptions({ path: { eventId } }),
    enabled: !isAuthenticated && !authLoading,
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000,
    staleTime: 0,
  });

  const { data: leaderboard, isLoading: leaderboardLoading } = useQuery({
    ...getPickemLeaderboardOptions({ path: { eventId } }),
    enabled: !draftPreviewEnabled,
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000,
    staleTime: 0,
  });

  const submitMutation = useMutation({
    ...submitPickemPicksMutation(),
    ...(draftPreviewEnabled ? { mutationFn: savePreviewPicks } : {}),
    // Optimistically apply the submitted picks to the cache so the next pick
    // builds its merged set from FRESH data, not the pre-mutation snapshot.
    // Without this, rapid back-to-back picks read stale `userPicksMap`,
    // re-submit a smaller payload, and the backend's full-replace semantics
    // wipe the earlier pick. (See scoring-bug investigation 2026-05-21.)
    onMutate: async (variables) => {
      const queryKey = getPickemUserBracketQueryKey({ path: { eventId } });
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      const submittedPicks = variables.body?.picks ?? [];
      queryClient.setQueryData(queryKey, (old: unknown) => {
        if (!old || typeof old !== "object") return old;
        const cast = old as { userPicks?: PickemUserPick[] };
        const nowIso = new Date().toISOString();
        const optimisticUserId = user?.id ?? "_optimistic_user";
        return {
          ...cast,
          ...(variables.body?.lineup !== undefined
            ? { userLineup: variables.body.lineup }
            : {}),
          userPicks: submittedPicks.map((p) => ({
            id: `_optimistic_${p.bracketMatchId}`,
            eventId,
            userId: optimisticUserId,
            bracketMatchId: p.bracketMatchId,
            pickedCompetitorId: p.pickedCompetitorId,
            predictedLoserScore: p.predictedLoserScore ?? null,
            predictedReset: p.predictedReset ?? null,
            createdAt: nowIso,
            updatedAt: nowIso,
          })),
        };
      });
      const lineupChanged =
        variables.body?.lineup !== undefined &&
        JSON.stringify(variables.body.lineup) !==
          JSON.stringify(
            (previous as { userLineup?: LineupSlot[] } | undefined)?.userLineup ?? [],
          );
      return { previous, lineupChanged };
    },
    onError: (err, _vars, ctx) => {
      const queryKey = getPickemUserBracketQueryKey({ path: { eventId } });
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
      toast.error(err instanceof Error ? err.message : "Failed to save pick");
    },
    onSuccess: (_data, _variables, ctx) => {
      queryClient.invalidateQueries({
        queryKey: getPickemUserBracketQueryKey({ path: { eventId } }),
      });
      if (!ctx?.lineupChanged) toast.success("Pick saved");
    },
  });

  const [pickingMatch, setPickingMatch] = useState<{
    match: PickemBracketMatch;
    topId?: string;
    bottomId?: string;
  } | null>(null);
  const [showActual, setShowActual] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const bracketRef = useRef<HTMLDivElement>(null);
  const { isSubmitted, markSubmitted } = usePickemSubmitted(
    eventId,
    draftPreviewEnabled ? "local-preview" : user?.id,
  );

  const activeQuery = isAuthenticated ? userBracketQuery : publicDetailQuery;
  const bracketPayload: BracketPayload | null = useMemo(() => {
    if (isAuthenticated) {
      const data = userBracketQuery.data;
      if (!data) return null;
      return {
        event: data.event,
        competitors: data.competitors,
        bracketMatches: data.bracketMatches,
        userPicks: data.userPicks,
      };
    }
    const data = publicDetailQuery.data;
    if (!data) return null;
    return {
      event: data.event,
      competitors: data.competitors,
      bracketMatches: data.bracketMatches,
      userPicks: [],
    };
  }, [isAuthenticated, userBracketQuery.data, publicDetailQuery.data]);

  const userPicksMap = useMemo(() => {
    if (!bracketPayload) return {};
    const map: Record<string, string> = {};
    for (const pick of bracketPayload.userPicks) {
      map[pick.bracketMatchId] = pick.pickedCompetitorId;
    }
    return map;
  }, [bracketPayload]);

  // Predicted loser score per finals pick. Stored in the same Record-keyed-by-
  // matchId shape as userPicksMap so they stay in lockstep when picks change.
  const userLoserScoresMap = useMemo(() => {
    if (!bracketPayload) return {};
    const map: Record<string, number | null> = {};
    for (const pick of bracketPayload.userPicks) {
      if (pick.predictedLoserScore != null) {
        map[pick.bracketMatchId] = pick.predictedLoserScore;
      }
    }
    return map;
  }, [bracketPayload]);

  // Predicted reset per finals pick (Grand Final only). Same Record-keyed-by-
  // matchId shape as userLoserScoresMap so they stay in lockstep.
  const userResetsMap = useMemo(() => {
    if (!bracketPayload) return {};
    const map: Record<string, boolean | null> = {};
    for (const pick of bracketPayload.userPicks) {
      if (pick.predictedReset != null) {
        map[pick.bracketMatchId] = pick.predictedReset;
      }
    }
    return map;
  }, [bracketPayload]);

  // The finals match id — used by the pick modal to switch into "predict the
  // loser score too" mode and by the bracket card to show the predicted score.
  const finalsMatchId = useMemo(() => {
    if (!bracketPayload) return null;
    return findFinalsMatchId(bracketPayload.bracketMatches);
  }, [bracketPayload]);

  // Per-match points so each correct pick's card can show "+X" overhead.
  // Recomputes whenever picks or bracket state change.
  const userPointsByMatchId = useMemo(() => {
    if (!bracketPayload) return {};
    return computePointsByMatchId(
      bracketPayload.bracketMatches,
      userPicksMap,
      userLoserScoresMap,
      userResetsMap,
      bracketPayload.event.pointsPerRound,
    );
  }, [bracketPayload, userPicksMap, userLoserScoresMap, userResetsMap]);

  if (authLoading || activeQuery.isLoading) {
    return (
      <div className="min-h-[60vh] w-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (activeQuery.error || !bracketPayload) {
    return (
      <div className="min-h-[60vh] w-full flex flex-col items-center justify-center gap-3">
        <p className="text-muted-foreground">Event not found.</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/pickem">Back to events</Link>
        </Button>
      </div>
    );
  }

  const {
    event,
    competitors: sourceCompetitors,
    bracketMatches,
  } = bracketPayload;
  const competitors = sourceCompetitors.map((c) => ({
    ...c,
    imageUrl:
      c.imageUrl || c.startggProfile?.participants[0]?.avatarUrl || null,
  }));
  const userLineup = userBracketQuery.data?.userLineup ?? [];
  const predictionMatches = event.allowUserLineup
    ? personalBracket(bracketMatches, userLineup, event.bracketFormat).map(
        (m) => ({
          ...m,
          status: "pending" as const,
          actualLoserScore: null,
          actualReset: null,
          scoreTop: null,
          scoreBottom: null,
        }),
      )
    : bracketMatches;
  const displayMatches =
    event.allowUserLineup && !showActual ? predictionMatches : bracketMatches;
  const changeLineup = (lineup: LineupSlot[]) => {
    const valid = prunePicks(
      personalBracket(bracketMatches, lineup, event.bracketFormat),
      userPicksMap,
      event.bracketFormat,
    );
    submitMutation.mutate({
      path: { eventId },
      body: {
        lineup,
        picks: Object.entries(valid).map(
          ([bracketMatchId, pickedCompetitorId]) => ({
            bracketMatchId,
            pickedCompetitorId,
            predictedLoserScore:
              userLoserScoresMap[bracketMatchId] ?? undefined,
            predictedReset: userResetsMap[bracketMatchId] ?? undefined,
          }),
        ),
      },
    });
  };
  const userBracketScore = userBracketQuery.data?.score ?? null;
  const score = userBracketScore ?? {
    userId: user?.id ?? "",
    totalPoints: 0,
    bracketPoints: 0,
    placementPoints: 0,
    correctPicks: 0,
    totalPicks: 0,
  };
  const status = getEffectiveStatus(event);
  const canPick =
    draftPreviewEnabled || (isAuthenticated && status === "active");

  const lockDate = event.lockDate ? new Date(event.lockDate) : null;
  const publishDate = event.publishDate ? new Date(event.publishDate) : null;

  let timingLine: string | null = null;
  if (status === "scheduled") {
    timingLine = `Opens ${formatRelativeTime(publishDate) ?? "soon"}`;
  } else if (status === "active") {
    const rel = formatRelativeTime(lockDate);
    timingLine = rel ? `Locks ${rel}` : "Open for picks";
  } else if (status === "locked") {
    timingLine = "Picks locked — awaiting results";
  } else if (status === "completed") {
    timingLine = "Event complete";
  }

  const handlePickMatch = (
    match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => {
    if (
      !canPick ||
      submitMutation.isPending ||
      (event.allowUserLineup && showActual)
    )
      return;
    setPickingMatch({ match, topId: ctx.topId, bottomId: ctx.bottomId });
  };

  const canPickMatch = (
    _match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => {
    if (
      !canPick ||
      submitMutation.isPending ||
      (event.allowUserLineup && showActual)
    )
      return false;
    return !!ctx.topId && !!ctx.bottomId;
  };

  const handleSelectCompetitor = (
    competitorId: string,
    predictedLoserScore: number | null = null,
    predictedReset: boolean | null = null,
  ) => {
    if (!pickingMatch) return;
    const merged: Record<string, string> = { ...userPicksMap };
    merged[pickingMatch.match.id] = competitorId;

    // Mirror picks → also rebuild the score + reset maps. Only the finals pick
    // is allowed a non-null score/reset; everything else stays null.
    const mergedScores: Record<string, number | null> = {
      ...userLoserScoresMap,
    };
    const mergedResets: Record<string, boolean | null> = {
      ...userResetsMap,
    };
    if (pickingMatch.match.id === finalsMatchId) {
      mergedScores[pickingMatch.match.id] = predictedLoserScore;
      mergedResets[pickingMatch.match.id] = predictedReset;
    }

    // Prune downstream picks orphaned by this change. Iterate to a fixed point
    // because removing one orphan can orphan another deeper in the bracket.
    const removed = new Set<string>();
    for (let iter = 0; iter < 10; iter++) {
      const newEffective = computeEffectiveCompetitors(
        predictionMatches,
        merged,
        event.bracketFormat,
      );
      const toDelete: string[] = [];
      for (const [matchId, pickedId] of Object.entries(merged)) {
        if (matchId === pickingMatch.match.id) continue;
        const match = predictionMatches.find((m) => m.id === matchId);
        if (!match) continue;
        const topId = match.competitorTopId ?? newEffective.get(matchId)?.topId;
        const bottomId =
          match.competitorBottomId ?? newEffective.get(matchId)?.bottomId;
        if (pickedId !== topId && pickedId !== bottomId) {
          toDelete.push(matchId);
        }
      }
      if (toDelete.length === 0) break;
      for (const id of toDelete) {
        delete merged[id];
        delete mergedScores[id];
        delete mergedResets[id];
        removed.add(id);
      }
    }

    submitMutation.mutate({
      path: { eventId },
      body: {
        ...(event.allowUserLineup ? { lineup: userLineup } : {}),
        picks: Object.entries(merged).map(
          ([bracketMatchId, pickedCompetitorId]) => ({
            bracketMatchId,
            pickedCompetitorId,
            // Generated DTO strips `| null` from nullable fields, so omit the
            // property entirely when unset. Backend zod accepts both undefined
            // and null — they mean the same thing.
            predictedLoserScore: mergedScores[bracketMatchId] ?? undefined,
            predictedReset: mergedResets[bracketMatchId] ?? undefined,
          }),
        ),
      },
    });

    if (removed.size > 0) {
      toast.info(
        `Cleared ${removed.size} downstream pick${removed.size === 1 ? "" : "s"} that no longer fit the bracket.`,
      );
    }

    setPickingMatch(null);
  };

  const pickProgress = (() => {
    const effective = computeEffectiveCompetitors(
      predictionMatches,
      userPicksMap,
      event.bracketFormat,
    );
    const pickable = predictionMatches.filter((m) => {
      const eff = effective.get(m.id);
      return !!eff?.topId && !!eff?.bottomId;
    }).length;
    const picked = Object.keys(userPicksMap).length;
    return {
      picked,
      pickable: event.allowUserLineup ? bracketMatches.length : pickable,
    };
  })();

  const userRank = (() => {
    if (!leaderboard || !user) return null;
    const idx = leaderboard.findIndex((e) => e.userId === user.id);
    return idx === -1 ? null : idx + 1;
  })();

  return (
    <div
      className={cn(
        "max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-4",
        // Reserve room for the mobile sticky submit bar so it doesn't cover
        // the bottom of the bracket scroll area.
        isAuthenticated && canPick && "pb-24 md:pb-8",
      )}
    >
      {/* Header — event image as background, content overlaid */}
      {draftPreviewEnabled && (
        <p className="rounded-lg border p-3 text-sm">
          Local draft preview. Picks save only in this browser; deadlines are
          bypassed for testing. Sharing and live scoring are unavailable.
        </p>
      )}
      <div className="relative w-full h-40 sm:h-48 shrink-0 overflow-hidden rounded-xl bg-muted">
        <img
          src={event.bannerUrl || event.imageUrl || PickemPlaceholderUrl}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/60 to-background/20" />
        <div className="relative z-10 h-full flex items-end gap-3 p-3 sm:p-4">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="bg-background/40 backdrop-blur-sm hover:bg-background/60"
          >
            <Link to="/pickem">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          {event.logoUrl && (
            <img
              src={event.logoUrl}
              alt={`${event.name} logo`}
              className="h-12 w-12 sm:h-16 sm:w-16 shrink-0 rounded-lg bg-background/90 object-contain"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold truncate drop-shadow-md">
                {event.name}
              </h1>
              <span
                className={cn(
                  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                  effectiveStatusBadgeClass[status],
                )}
              >
                {effectiveStatusLabel[status]}
              </span>
              <PickemScoringInfo
                placementScoring={event.allowUserLineup && !!event.startggEventId}
                pointsPerRound={event.pointsPerRound}
                startingRound={event.startingRound}
                bracketFormat={event.bracketFormat}
              />
            </div>
            {timingLine && (
              <p className="text-xs text-foreground/80 mt-0.5 drop-shadow">
                {timingLine}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="bg-background/40 backdrop-blur-sm hover:bg-background/60"
            onClick={() => setShareOpen(true)}
            disabled={draftPreviewEnabled}
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      </div>

      <SharePickemDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        eventPageUrl={typeof window !== "undefined" ? window.location.href : ""}
        openExternalUrl={(url) =>
          window.open(url, "_blank", "noopener,noreferrer")
        }
        imageCapture={
          isAuthenticated && bracketMatches.length > 0
            ? {
                bracketRef,
                eventId,
                getAuthToken: getStoredToken,
                apiBaseUrl: PUBLIC_API_BASE_URL,
              }
            : undefined
        }
        context={{
          eventName: event.name,
          picksMade: isAuthenticated ? pickProgress.picked : undefined,
          totalPicks: isAuthenticated ? pickProgress.pickable : undefined,
          points: isAuthenticated ? score.totalPoints : undefined,
          rank: isAuthenticated ? userRank : undefined,
          url: typeof window !== "undefined" ? window.location.href : undefined,
        }}
      />

      <SubmitPicksCelebrationDialog
        open={celebrationOpen}
        onOpenChange={setCelebrationOpen}
        eventName={event.name}
        picksMade={pickProgress.picked}
        totalPicks={pickProgress.pickable}
        onShare={() => {
          setCelebrationOpen(false);
          if (!draftPreviewEnabled) setShareOpen(true);
        }}
      />

      {!isAuthenticated && <SignInBanner status={status} />}

      {isAuthenticated ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label="Your Points"
            value={score.totalPicks === 0 && userLineup.length === 0 ? "—" : score.totalPoints.toString()}
            hint={
              event.allowUserLineup && event.startggEventId
                ? `${score.bracketPoints ?? score.totalPoints} bracket + ${score.placementPoints ?? 0} placement`
                : score.totalPicks === 0
                ? "No picks submitted"
                : `${score.correctPicks} / ${score.totalPicks} correct`
            }
          />
          <SummaryCard
            label="Your Rank"
            value={userRank ? `#${userRank}` : "—"}
            hint={
              userRank
                ? `of ${leaderboard?.length ?? 0}`
                : score.totalPicks === 0
                  ? "Not ranked — pick to enter"
                  : leaderboard && leaderboard.length > 0
                    ? `of ${leaderboard.length}`
                    : "No entries yet"
            }
          />
          <SummaryCard
            label="Picks Made"
            value={`${pickProgress.picked} / ${pickProgress.pickable}`}
            hint={canPick ? "Click a match to pick" : "Locked"}
          />
          <SummaryCard
            label="Competitors"
            value={competitors.length.toString()}
            hint={`${bracketMatches.length} matches`}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label="Competitors"
            value={competitors.length.toString()}
            hint={`${bracketMatches.length} matches`}
          />
          <SummaryCard
            label="Players"
            value={(leaderboard?.length ?? 0).toString()}
            hint={
              leaderboard && leaderboard.length > 0
                ? "on the board"
                : "No entries yet"
            }
          />
          <SummaryCard
            label="Leader"
            value={(leaderboard?.[0]?.totalPoints ?? 0).toString()}
            hint="pts"
          />
          <SummaryCard
            label="Status"
            value={effectiveStatusLabel[status]}
            hint={timingLine ?? undefined}
          />
        </div>
      )}

      {event.startggUrl && (
        <div className="text-sm text-muted-foreground">
          <a
            className="underline"
            href={event.startggUrl}
            target="_blank"
            rel="noreferrer"
          >
            Tournament on start.gg
          </a>
          {event.startggLastSyncAt && (
            <span>
              {" "}
              · Updated {new Date(event.startggLastSyncAt).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}
      {event.allowUserLineup && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              variant={!showActual ? "default" : "outline"}
              onClick={() => setShowActual(false)}
            >
              Your prediction
            </Button>
            <Button
              variant={showActual ? "default" : "outline"}
              onClick={() => setShowActual(true)}
            >
              Live results
            </Button>
          </div>
          {!showActual && (
            <PlayerPool
              matches={bracketMatches}
              format={event.bracketFormat}
              bracketLayout={event.bracketLayout}
              userPicks={userPicksMap}
              competitors={competitors}
              lineup={userLineup}
              disabled={!canPick || submitMutation.isPending}
              saving={submitMutation.isPending}
              disabledReason={
                !isAuthenticated
                  ? "Sign in to choose your players. You can browse the pool first."
                  : status === "scheduled"
                    ? "Player selection opens when this event starts accepting picks."
                    : "Picks are closed. You can review your lineup and browse player profiles."
              }
              onChange={changeLineup}
            />
          )}
        </div>
      )}
      <Tabs defaultValue="bracket" className="flex-1 flex flex-col min-h-0">
        <TabsList className="w-fit">
          <TabsTrigger value="bracket">Bracket</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
        </TabsList>

        <TabsContent value="bracket" className="flex-1 overflow-auto mt-3">
          {bracketMatches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Trophy className="h-10 w-10 text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">
                The bracket hasn't been set up yet.
              </p>
            </div>
          ) : (
            <div ref={bracketRef} className="pb-4">
              <PickemBracket
                showLineupNumbers={event.allowUserLineup && !showActual}
                matches={displayMatches}
                competitors={competitors}
                userPicks={isAuthenticated ? userPicksMap : undefined}
                userPredictedLoserScores={
                  isAuthenticated ? userLoserScoresMap : undefined
                }
                userPredictedResets={
                  isAuthenticated ? userResetsMap : undefined
                }
                userPointsByMatchId={
                  isAuthenticated ? userPointsByMatchId : undefined
                }
                bracketLayout={event.bracketLayout}
                bracketFormat={event.bracketFormat}
                onPickMatch={isAuthenticated ? handlePickMatch : undefined}
                canPickMatch={isAuthenticated ? canPickMatch : undefined}
              />
            </div>
          )}
        </TabsContent>

        <TabsContent
          value="leaderboard"
          className="flex-1 min-h-0 mt-3 overflow-hidden flex flex-col"
        >
          {leaderboardLoading ? (
            <LeaderboardSkeleton />
          ) : (
            <PickemLeaderboard
              entries={leaderboard ?? []}
              currentUserId={user?.id}
            />
          )}
        </TabsContent>
      </Tabs>

      {isAuthenticated && canPick && (
        <>
          <div className="hidden md:block">
            <SubmitPicksButton
              layout="inline"
              picksMade={pickProgress.picked}
              totalPicks={pickProgress.pickable}
              isSubmitted={isSubmitted}
              onSubmit={() => {
                markSubmitted();
                setCelebrationOpen(true);
              }}
            />
          </div>
          <div className="md:hidden">
            <SubmitPicksButton
              layout="sticky"
              picksMade={pickProgress.picked}
              totalPicks={pickProgress.pickable}
              isSubmitted={isSubmitted}
              onSubmit={() => {
                markSubmitted();
                setCelebrationOpen(true);
              }}
            />
          </div>
        </>
      )}

      <PickDialog
        pickTarget={pickingMatch}
        competitors={competitors}
        currentPickId={
          pickingMatch ? userPicksMap[pickingMatch.match.id] : undefined
        }
        currentLoserScore={
          pickingMatch
            ? (userLoserScoresMap[pickingMatch.match.id] ?? null)
            : null
        }
        currentReset={
          pickingMatch ? (userResetsMap[pickingMatch.match.id] ?? null) : null
        }
        isFinal={pickingMatch?.match.id === finalsMatchId}
        isGrandFinal={pickingMatch?.match.section === "grand_final"}
        isSubmitting={submitMutation.isPending}
        onSelect={handleSelectCompetitor}
        onClose={() => setPickingMatch(null)}
      />
    </div>
  );
}

function SignInBanner({
  status,
}: {
  status: ReturnType<typeof getEffectiveStatus>;
}) {
  const [signInError, setSignInError] = useState<string | null>(null);
  const isLocked = status === "locked" || status === "completed";

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Lock className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {isLocked
              ? "Sign in to compete next time"
              : "Sign in to make picks"}
          </p>
          <p className="text-xs text-muted-foreground">
            {isLocked
              ? "You're viewing the bracket as a guest. Picks are closed for this event."
              : "You can browse the bracket as a guest. Sign in to lock in your predictions."}
          </p>
        </div>
      </div>
      <div className="flex flex-col items-stretch sm:items-end gap-1 sm:min-w-[260px]">
        <GoogleSignInButton
          onError={setSignInError}
          stayOnPage
          className="sm:max-w-none"
        />
        {signInError && (
          <p role="alert" className="text-xs text-destructive sm:text-right">
            {signInError}
          </p>
        )}
      </div>
    </div>
  );
}

function LeaderboardSkeleton() {
  return (
    <div className="flex flex-col gap-4 animate-pulse">
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 rounded-md bg-card border" />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 items-end pt-3">
        <div className="h-28 rounded-lg bg-card border" />
        <div className="h-32 rounded-lg bg-card border" />
        <div className="h-28 rounded-lg bg-card border" />
      </div>
      <div className="space-y-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-11 rounded-md bg-card border" />
        ))}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="bg-card border rounded-lg p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-xl font-bold tabular-nums mt-1">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function PickDialog({
  pickTarget,
  competitors,
  currentPickId,
  currentLoserScore,
  currentReset,
  isFinal,
  isGrandFinal,
  isSubmitting,
  onSelect,
  onClose,
}: {
  pickTarget: {
    match: PickemBracketMatch;
    topId?: string;
    bottomId?: string;
  } | null;
  competitors: PickemCompetitor[];
  currentPickId: string | undefined;
  currentLoserScore: number | null;
  currentReset: boolean | null;
  isFinal: boolean;
  isGrandFinal: boolean;
  isSubmitting: boolean;
  onSelect: (
    competitorId: string,
    predictedLoserScore?: number | null,
    predictedReset?: boolean | null,
  ) => void;
  onClose: () => void;
}) {
  const competitorMap = useMemo(
    () => new Map(competitors.map((c) => [c.id, c])),
    [competitors],
  );

  const top = pickTarget?.topId ? competitorMap.get(pickTarget.topId) : null;
  const bottom = pickTarget?.bottomId
    ? competitorMap.get(pickTarget.bottomId)
    : null;

  // Finals: the dialog manages two pieces of state internally (winner + loser
  // score) and only commits on "Save pick". Reset every time the dialog opens
  // so we don't leak between matches.
  const [selectedWinnerId, setSelectedWinnerId] = useState<string | null>(null);
  const [loserScore, setLoserScore] = useState<number>(0);
  const [reset, setReset] = useState<boolean>(false);

  // The Grand Final is first-to-3 (winner 3, loser 0–2); single-elim finals are
  // first-to-5 (winner 5, loser 0–4).
  const winTarget = isGrandFinal ? 3 : 5;
  const loserMax = winTarget - 1;

  useEffect(() => {
    if (!pickTarget) return;
    setSelectedWinnerId(currentPickId ?? null);
    setLoserScore(currentLoserScore ?? 0);
    setReset(currentReset ?? false);
  }, [pickTarget, currentPickId, currentLoserScore, currentReset]);

  // Non-finals: keep the existing one-click-to-submit behavior. Finals needs
  // a confirm step so the score input can be edited before saving.
  const handleClickCompetitor = (competitorId: string) => {
    if (isFinal) {
      setSelectedWinnerId(competitorId);
      return;
    }
    onSelect(competitorId);
  };

  const handleConfirmFinals = () => {
    if (!selectedWinnerId) return;
    const clampedScore = Math.max(0, Math.min(loserMax, loserScore));
    // Reset is a Grand-Final-only concept — send null for single-elim finals.
    onSelect(selectedWinnerId, clampedScore, isGrandFinal ? reset : null);
  };

  return (
    <Dialog
      open={!!pickTarget}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {isFinal ? "Pick the winner & final score" : "Pick the winner"}
          </DialogTitle>
          <DialogDescription>
            {isGrandFinal
              ? "Pick the champion, the loser's final-set score (0–2) and whether it goes to a bracket reset. Nail the reset + score for a +5 bonus."
              : isFinal
                ? "Select your winner, then predict loser's score (0-4). The closer your prediction, the more points you earn"
                : "Choose who you think will win this match."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 pt-2">
          {[top, bottom].map((competitor) => {
            if (!competitor) return null;
            const isPickedWinner = isFinal
              ? selectedWinnerId === competitor.id
              : currentPickId === competitor.id;
            const isLoserSlot =
              isFinal && selectedWinnerId !== null && !isPickedWinner;
            return (
              <button
                key={competitor.id}
                type="button"
                disabled={isSubmitting}
                onClick={() => handleClickCompetitor(competitor.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-md border border-border bg-card text-left cursor-pointer transition-colors",
                  "hover:border-primary hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed",
                  isPickedWinner && "border-primary bg-primary/10",
                )}
              >
                {competitor.imageUrl ? (
                  <img
                    src={competitor.imageUrl}
                    alt={competitor.name}
                    className="h-10 w-10 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 rounded bg-muted flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {competitor.name.charAt(0)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {competitor.name}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Seed {competitor.seed}
                  </p>
                </div>
                {isFinal && isPickedWinner && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Score
                    </span>
                    <span className="font-bold text-lg text-primary tabular-nums">
                      {winTarget}
                    </span>
                  </div>
                )}
                {isLoserSlot && (
                  <div
                    className="flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Score
                    </span>
                    <ScoreSelector
                      value={loserScore}
                      onChange={setLoserScore}
                      max={loserMax}
                      ariaLabel="Predicted loser score"
                    />
                  </div>
                )}
                {!isFinal && isPickedWinner && (
                  <span className="text-[10px] uppercase tracking-wide text-primary font-medium">
                    Current
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {isGrandFinal && selectedWinnerId && (
          <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
            <p className="text-xs font-semibold mb-0.5">Bracket reset?</p>
            <p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">
              Will the lower-bracket team force a deciding second series? Get
              this <span className="font-medium text-foreground">and</span> the
              score right for a{" "}
              <span className="text-amber-400 font-bold">+5</span> bonus.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Reset", value: true },
                { label: "No reset", value: false },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setReset(opt.value)}
                  className={cn(
                    "h-9 rounded-md border text-sm font-medium transition-colors",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    reset === opt.value
                      ? "border-amber-500 bg-amber-500/15 text-amber-400"
                      : "border-border bg-card hover:border-amber-500/60",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {isFinal && (
          <div className="flex justify-end gap-2 pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmFinals}
              disabled={!selectedWinnerId || isSubmitting}
            >
              Save pick
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
